// Third-party Imports
import type { I18nProvider } from '@refinedev/core'
import { MODULE_MANIFESTS } from '@coresoft/platform-contract'

// Lang Imports
import type { UiLocale } from '@/lang/locales'
import { DEFAULT_UI_LOCALE, readUiLocale, rememberUiLocale, parseUiLocale } from '@/lang/locales'
import { lookup } from '@/lang/dictionary'

/*
 * Resource нэр → хүн уншихуйц шошго.
 *
 * Refine мэдэгдэлдээ resource-ийн МАШИН нэрийг тавьдаг («hr.employee-level»).
 * Манифест бүрд аль хэдийн монгол шошго байгаа тул түүнийг ашиглана.
 *
 * ! Refine мэдэгдэлд НЭГ тоог хэрэглэдэг: `hr.employee-levels` → `hr.employee-level`.
 * ! Тиймээс индексэд хоёуланг нь тавина.
 */
const RESOURCE_LABELS = new Map<string, string>()

for (const manifest of MODULE_MANIFESTS) {
  for (const resource of manifest.resources) {
    RESOURCE_LABELS.set(resource.name, resource.label)
    RESOURCE_LABELS.set(resource.name.replace(/s$/, ''), resource.label)
  }
}

const labelOf = (value: unknown): string => {
  const name = String(value ?? '')

  return RESOURCE_LABELS.get(name) ?? name
}

/*
 * Refine нь resource-ийн шошгыг ӨӨРӨӨ орчуулах гэж `<resource>.<resource>`
 * хэлбэрийн түлхүүр асуудаг. Үүнийг таньж шошгоор солихгүй бол мэдэгдэлд
 * «hr.employment-types.hr.employment-types» гэж давхарласан машин нэр гарна.
 */
const DOUBLED = /^(.+)\.\1$/

/*
 * МЭДЭГДЛИЙН ТҮЛХҮҮРҮҮД — текст нь одоо `notifications.*`-д, ЛОГИК нь энд.
 *
 * Refine нь `translate('notifications.createSuccess', { resource })` гэж дууддаг.
 * Хоёр зүйлийг хэлний сан ӨӨРӨӨ хийж чадахгүй тул энд үлдэв:
 *   ① `resource` нь МАШИН нэр — хайхаас ӨМНӨ шошгоор солино;
 *   ② `statusCode` нь БАЙЖ Ч БОЛНО, БАЙХГҮЙ Ч БОЛНО — нэг мөрөнд нөхцөл
 *      бичих боломжгүй тул хоёр түлхүүр болгов.
 *
 * ! Монгол дээрх ГАРАХ МӨРҮҮД ХӨНДӨГДӨӨГҮЙ — `mn.json`-д яг тэр бичвэр байна.
 * ! Хатуу бичсэн мөрийг САНД зөөсөн нь мэдэгдэл хэл сэлгэхэд хамт сэлгэх
 * ! боломжийг нэмсэн; монгол панелийн хувьд гарц нь БИТ ТУТАМДАА ижил.
 */
const NOTIFICATION_PREFIX = 'notifications.'

const notificationKey = (key: string, params?: Record<string, unknown>): string =>
  key === 'notifications.error' && params?.statusCode !== undefined ? 'notifications.errorWithCode' : key

const notificationParams = (params?: Record<string, unknown>): Record<string, unknown> | undefined =>
  params && 'resource' in params ? { ...params, resource: labelOf(params.resource) } : params

/*
 * ХЭЛИЙГ УНШИХ.
 *
 * ! Кэш нь ЗӨВХӨН ХӨТЧИД. `translate` нь дэлгэц зурах бүрд шошго тутамд
 * ! дуудагддаг тул `document.cookie`-г мөр болгон уншуулах нь үнэтэй; сонголт
 * ! солигдоход хуудас БҮТНЭЭР дахин ачаалагддаг (`changeLocale`) тул кэш
 * ! хэзээ ч хоцрогдохгүй. Серверт кэшлэвэл модулийн санах ой хүсэлт хооронд
 * ! хуваалцагддаг тул НЭГ хэрэглэгчийн сонголт бусдад асгарна — тиймээс
 * ! серверт үргэлж анхдагчийг буцаана.
 */
let cachedLocale: UiLocale | undefined

const activeLocale = (): UiLocale => {
  if (typeof document === 'undefined') return DEFAULT_UI_LOCALE

  return (cachedLocale ??= readUiLocale())
}

/*
 * ОЛДООГҮЙ ТҮЛХҮҮРИЙГ ЗААВАЛ НЭГ УДАА л хашгирна.
 *
 * ! Түлхүүр бүрийг нэг л удаа мэдээлнэ — эс тэгвэл жагсаалт зурах үед нэг
 * ! мөрөнд нэг анхааруулга гарч консол хэрэглэх боломжгүй болно.
 */
const warned = new Set<string>()

const warnMissing = (key: string) => {
  if (process.env.NODE_ENV === 'production' || warned.has(key)) return

  warned.add(key)
  console.warn(`[i18n] түлхүүр хэлний санд алга: ${key}`)
}

export const i18nProvider: I18nProvider = {
  /*
   * ХАЙЛТЫН ДАРААЛАЛ:
   *   ① мэдэгдлийн түлхүүр (Refine-ийн гэрээ — resource нэрийг шошго болгоно)
   *   ② давхарласан resource түлхүүр
   *   ③ хэлний сан: тухайн хэл → эх хэл `mn`
   *   ④ Refine-ийн/дуудагчийн `defaultMessage`
   *   ⑤ түлхүүрийг өөрийг + dev дээр анхааруулга
   *
   * ! ③ нь ④-өөс ДЭЭГҮҮР: `defaultMessage` бол дэлгэцэд ХАТУУ бичигдсэн хуулбар —
   * ! яг түүнээс салах гэж энэ давхаргыг барьж байна. Дээгүүр тавибал `mn.json`-г
   * ! засахад дэлгэц ХАРИУ ӨГӨХГҮЙ болж, сан нь чимээгүй хуурамч болно.
   *
   * ! ⑤-д хүрэх нь ХӨГЖҮҮЛЭГЧИЙН АЛДАА (аль ч хэлэнд мөр байхгүй). Түлхүүрийг
   * ! харуулах нь гоё биш ч ХООСОН орхихоос дээр: хоосон товч дарагдахгүй, хоосон
   * ! гарчиг эвдэрсэн эсэхийг нь хэлж чадахгүй.
   */
  translate: (key, options, defaultMessage) => {
    /*
     * ! Refine-ийн `useTranslate` нь ХОЁР хэлбэртэй: `t(key, params, default)` ба
     * ! `t(key, default)`. Хоёр дахь хэлбэрт `options` нь МӨР болж ирнэ. Refine
     * ! өөрөө зөвхөн бидний хариу `null/undefined` байвал нөхдөг — бид түлхүүр
     * ! буцаадаг тул ЭНД өөрсдөө танихгүй бол `t('common.action.save', 'Хадгалах')`
     * ! гэж бичсэн дэлгэц түлхүүрийг зурах болно.
     */
    const inlineDefault = typeof options === 'string' && defaultMessage === undefined ? options : defaultMessage
    const params = (typeof options === 'string' ? undefined : options) as Record<string, unknown> | undefined

    const locale = activeLocale()

    if (key.startsWith(NOTIFICATION_PREFIX)) {
      const found = lookup(locale, notificationKey(key, params), notificationParams(params))

      if (found !== undefined) return found
    }

    const doubled = DOUBLED.exec(key)

    if (doubled) return labelOf(doubled[1])

    const found = lookup(locale, key, params)

    if (found !== undefined) return found

    if (inlineDefault !== undefined) return inlineDefault

    warnMissing(key)

    return key
  },

  getLocale: activeLocale,

  /**
   * Хэл солих — cookie бичээд хуудсыг БҮТНЭЭР дахин ачаална.
   *
   * ! Refine-ийн `useTranslate` нь `[i18nProvider]`-ээр memo хийдэг. Provider-ийн
   * ! объект хэзээ ч солигддоггүй тул хэл солиход React ДАХИН ЗУРАХГҮЙ — зөвхөн
   * ! дараа зурагдах компонент шинэ хэлээр гарч, панел хоёр хэлээр хуваагдана.
   * ! Дахин ачаалах нь энэ гэрээний ГАНЦ шударга хариу (шинэ сан нэмэхгүйгээр).
   */
  changeLocale: locale => {
    const next = parseUiLocale(locale)

    if (next === activeLocale()) return Promise.resolve()

    rememberUiLocale(next)
    cachedLocale = next

    if (typeof window !== 'undefined') window.location.reload()

    return Promise.resolve()
  }
}
